import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';
import { mergeSx } from 'merge-sx';

import { OrganizationDTO, OrganizationDTOStatus } from '@/api/generated';
import { toOrganizationItems } from '@/root/linker';

import CountryDisplay from './CountryDisplay';
import OfficeDisplay from './OfficeDisplay';
import StatusDisplay from './StatusDisplay';

const commonStyles = {
  height: ({ spacing }) => spacing(7),
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  p: 2
} as SxProps<Theme>;

const withCommonStyles = (children: ReactNode) => <Box sx={commonStyles}>{children}</Box>;

export type OrganizationDTOWithTalents = OrganizationDTO & { talents: object; id: string };

const columnHelper = createColumnHelper<OrganizationDTOWithTalents>();

export const organizationColumns = (
  t: TFunction,
  handleEditDraftOrganization: (draftOrganizationSlug: string) => void,
  isMidotUser: boolean
) => {
  const officeColumn = columnHelper.accessor('offices', {
    header: () => t('layouts.organizations.columns.office'),
    cell: ({ getValue, row }) =>
      withCommonStyles(
        <OfficeDisplay value={getValue() ?? []} isArchived={row.original.status === OrganizationDTOStatus.INACTIVE} />
      )
  });

  return (
    [
      columnHelper.accessor('name', {
        header: () => t('layouts.organizations.columns.organization'),
        cell: ({ getValue, row }) => {
          const isDraft = row.original.status === OrganizationDTOStatus.DRAFT;

          return (
            <Box
              component={isDraft ? 'div' : Link}
              sx={mergeSx<Theme>(commonStyles, {
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main'
                }
              })}
              to={toOrganizationItems({ organizationSlug: row.original.slug! })}
              onClick={isDraft ? () => handleEditDraftOrganization(row.original.slug!) : undefined}
            >
              <Typography>{getValue()}</Typography>
            </Box>
          );
        }
      }),
      isMidotUser && officeColumn,
      columnHelper.accessor('legalName', {
        header: () => t('layouts.organizations.columns.legalName'),
        cell: ({ getValue }) => <Typography sx={commonStyles}>{getValue()}</Typography>
      }),
      columnHelper.accessor('countries', {
        header: () => t('layouts.organizations.columns.country'),
        cell: ({ getValue, row }) =>
          withCommonStyles(
            <CountryDisplay
              value={getValue() ?? []}
              isArchived={row.original.status === OrganizationDTOStatus.INACTIVE}
            />
          )
      }),
      columnHelper.accessor('status', {
        header: () => t('layouts.organizations.columns.status'),
        cell: ({ getValue }) => withCommonStyles(<StatusDisplay value={getValue()!} />)
      })
    ] as ColumnDef<OrganizationDTOWithTalents>[]
  ).filter(Boolean);
};
